import { Button, CardContent, Checkbox, FormControlLabel, Typography, CircularProgress } from "@mui/material";
import AppCard, { CARD_HEADER_SX } from "@plannel/components/ui/AppCard";
import Box from "@mui/material/Box";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState, useRef } from "react";
import { withTranslation } from "react-i18next";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import Snackbar from "@plannel/components/Snackbar";
import BaseConfigModal, { MODAL_SECTION_LABEL_SX } from "@plannel/components/modal/BaseConfigModal";
import SplitterContainer from "@plannel/components/SplitterContainer";
import DpVersionModal from "../dp-modal/DpVersionModal";
import DpCreateScenarios from "../dp-modal/DpCreateScenarios";
import dpProcessMgmtService from "@plannel/services/dp/dp-process-mgmt-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import planVersionService from "@plannel/services/data/plan-version-service";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";

import { isArray, isEmpty, isEqual, isNil, isObject, size, cloneDeep, includes, keys, entries } from "lodash";
import LoadingButton from "@mui/lab/LoadingButton";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AssessmentIcon from "@mui/icons-material/Assessment";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import { green, red, yellow } from "@mui/material/colors";
import { Circle, Close } from "@mui/icons-material";

import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import colorUtil from "@plannel/pages/utils/color-util";
import { useDispatch } from "react-redux";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import postnService from "@plannel/services/data/postn-service";
import userService from "@plannel/services/system/user-service";
import codeUtil from "@plannel/pages/utils/code-util";
import { FS } from "@plannel/utils/font-util";
 

/*
 * 번역값 안의 하드 개행(\n) 처리 — 두 가지를 구분한다. 어느 쪽도 번역값 자체는 안 건드린다(6개 언어 공통).
 *  · 안내문(MSG_RegenerateBf)의 \n 은 "줄", \n\n 은 문단 구분이라 그대로 세운다.
 *  · 체크박스 라벨(MSG_BfOverwriteSave)의 \n 은 옛 팝업 폭에 맞춘 강제 줄바꿈이라 문장 한가운데서 끊긴다
 *    ("Do you want to overwrite / the existing measure values / with ...") → 한 줄로 이어 붙이고 모달 폭에 맞춰 접는다.
 */
const splitLines = text => String(text ?? "").split(/\r\n|\r|\n/);
const joinLines = text => splitLines(text).map(line => line.trim()).filter(Boolean).join(" ");

/*
 * 카드 한 줄은 **번역 키와 원본 값**만 담고, 글자는 그릴 때 만든다.
 * 번역문을 카드 데이터(state)에 굳히면 언어를 바꿔도 state 가 그대로라
 * 계획을 다시 불러올 때(=새로고침)까지 옛 언어가 남는다.
 *   labelKey  : 앞에 붙는 이름의 번역 키(없으면 값만 그린다)
 *   value     : 번역이 필요 없는 값(날짜·버전명·개수)
 *   valueKey  : 값 자체가 번역 키인 경우(버킷 단위·시작 요일)
 *   template  : "[...]" 를 tokenKey 의 번역으로 바꿔 완성하는 수식 문자열
 */
const cardLine = (labelKey, value) => ({ labelKey, value });
const cardLineOfKey = (labelKey, valueKey) => ({ labelKey, valueKey });
const cardLineOfTemplate = (labelKey, template, tokenKey) => ({ labelKey, template, tokenKey });

const cardLineText = (t, line) => {
  if (isNil(line)) {
    return null;
  }
  if (!isObject(line)) {
    return line;
  }
  let value = line.value;
  if (!isNil(line.template)) {
    value = line.template.replace(/\[[^\]]*\]/g, t(line.tokenKey));
  } else if (!isNil(line.valueKey)) {
    value = t(line.valueKey);
  }
  const label = line.labelKey ? t(line.labelKey) : null;
  if (isNil(label)) {
    return value ?? "";
  }
  return isNil(value) ? label : `${label}: ${value}`;
};

/*
 * 카드 기본 틀. 렌더마다 새로 만들거나 setVerDatas 가 직접 수정하지 않도록 모듈 상수로 두고, 쓸 때는 항상 복제한다.
 * (예전에는 컴포넌트 본문의 배열을 setVerDatas 가 제자리에서 고쳐 그대로 돌려줘서, 첫 렌더의 배열이 state 와 같은 참조일 때
 *  setCardDatas 가 "변경 없음"으로 무시될 수 있었다.)
 */
const CARD_TEMPLATE = [
  { titleKey: "dpVersionInfo", text: "  ", key: "first" },
  { titleKey: "initialValue", text: " ", key: "second" },
  { titleKey: "forecastRunTime", text: [cardLine("startTs"), cardLine("endTs")], key: "third" },
  { titleKey: "forecastHorizon", text: [cardLine("startTs"), cardLine("endTs")], key: "fourth" },
  { titleKey: "totalCompletion", text: "  ", key: "fifth" },
];
const makeCardDatas = () => cloneDeep(CARD_TEMPLATE);

const DpProcessMgmt = ({ t, title, viewName }) => {
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();   // 권한 밖 화면으로 가는 버튼은 내보내지 않는다
  const reduxViewState = cloneDeep(reduxUtil.getViewState(viewName));
  const workbench = reduxUtil.getViewState(reduxUtil.createObjectState("DP_WORKBENCH"));

  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [versionOpen, setVersionOpen] = useState(false);
  const [scenarioOpen, setScenarioOpen] = useState(false);
  const [hasCurVersion, setHasCurVersion] = useState(true);
  const [selectVersion, setSelectVersion] = useState({});
  const [isCurrent, setIsCurrent] = useState(false);
  const [allCompleted, setAllCompleted] = useState(false);
  const [finishSave, setFinishSave] = useState(false);
  const [makeDemand, setMakeDemand] = useState(false);
  const [confirmPostnCd, setComfirmPostnCd] = useState("");
  const [approvalAccessControlFlg, setApprovalAccessControlFlg] = useState(false);
  const [isStartDp, setIsStartDp] = useState(false);
  const [isCreateVersion, setIsCreateVersion] = useState(false);
  const [refBfName, setRefBfName] = useState("");
  /*
   * overwriteFlg : overwrite 여부(check)
   * overwriteAlertFlg : overwrite message 를 띄울 것인지 여부 
   * initialBfFlg: INTIIAL DEMAND가 baselien forecast인지 여부
   * overWriteMeasure: overwrite할 지표
  */
  const [overwriteFlg, setOverwriteFlg] = useState(false);
  const [overwriteAlertFlg, setOverwriteAlertFlg] = useState(false);
  const [initialBfFlg, setInitialBfFlg] = useState(false);
  const [overWriteMeasure, setOverwriteMeasure] = useState([]);

  const [hrchyLevels, setHrchyLevels] = useState([]);
  const [chartDatas, setChartDatas] = useState([]);
  const [gridDatas, setGridDatas] = useState([]);
  const [postnByUser, setPostnByUser] = useState([]);
  const [userInfo, setUserInfo] = useState([]);
  const [bfParameters, setBfParameters] = useState([]);
  const [circleStyle, setCircleStyle] = useState({});
  const [statusValue, setStatusValue] = useState(100);
  const statusRef = useRef(100);
  const [userRoles, setUserRoles] = useState([]);

  const currentVersion = useRef({});
  const useBf = useRef(false);
  /*
   * 버전을 바꾸는 작업(makeDemand)이 끝날 때마다 1 증가한다.
   * 조회를 띄운 뒤 버전이 바뀌면 그 조회 응답은 "생성 전" 상태라 화면에 반영하면 안 된다.
   */
  const versionMutation = useRef(0);
  // retrieveDpProcess 호출 순번 — 마지막에 띄운 조회의 응답만 카드·차트·그리드에 반영한다.
  const dpProcessRequestSeq = useRef(0);

  const DP_INFO_INDEX = 0;
  const DP_INITIAL_VALUE_INDEX = 1;
  const BF_RUN_TIME_INDEX = 2;
  const DP_HORISON_INDEX = 3;
  const TOTAL_COMPLETE_INDEX = 4;

  const {
    dpVersion: { SCENARIO, DEFAULT, RELEASING, RUNNING },
    dpApproveStatus: { INPROGRESS, COMPLETED, NOTSTARTED },
    dpBfStatus: { PROCESSING, ERROR },
    dpMeasure: { BF_AMT, MF_AMT },
    dpConfig: { BF_PARAMETER },
    toggleType: { LINE },
    dpSettingCode: { BASELINE_FORECAST },
    roles,
    item, customer
  } = codeUtil;
  const { dpMeasure } = codeUtil;
  const valueByStatus = {
    100: 10,
    110: 20,
    120: 20,
    130: 30,
    140: 40,
    150: 60,
    160: 70,
    170: 90
  };
  const { colorSet, snackBarMessageColor: { confirm, networkError, saveFail, noMatchCondition, failAutoRun, saveSuccess } } = colorUtil;

  const { calculateRangeDate, formatDate, formatDateTime } = dateUtils;
  const userData = JSON.parse(localStorage.getItem("user"));
  const reduxState = reduxUtil.createObjectState(viewName);

  const getChartSeries = (hasBf) => {
    const bfColumn = { id: BF_AMT.name, field: BF_AMT.name, text: t(BF_AMT.name), type: LINE, orientation: "left", groupName: "Forecast", typeOptions: { fill: colorSet(0), barSize: 20 } };
    const chartSeries =  [
      { id: MF_AMT.name, field: MF_AMT.name, text: t(MF_AMT.name), type: LINE, orientation: "left", groupName: "Forecast", typeOptions: { fill: colorSet(1), barSize: 20 } },
    ];
    if (hasBf) {
      chartSeries.push(bfColumn);
    }
    return chartSeries;
  };

  const [cardDatas, setCardDatas] = useState(makeCardDatas);

  /*
   * 카드 일부만 바꿀 때는 호출 시점에 복제해 둔 cardDatas(클로저에 갇힌 옛 값)를 통째로 set 하지 말고,
   * 항상 "지금 최신 state" 위에 자기 카드만 덮어쓴다.
   * 버전 생성은 retrieveDpProcess / makeDemand / startBF 를 동시에 띄우는데, 각자 시작 시점의 cardDatas 를 복제해 두었다가
   * 나중에 끝난 쪽이 먼저 끝난 쪽이 바꾼 카드까지 옛 값으로 되돌려 버렸다(늦게 끝난 쪽 값이 이기는 문제).
   */
  const patchCardDatas = patch => {
    setCardDatas(prev => {
      const next = cloneDeep(prev);
      patch(next);
      return next;
    });
  };

  const retrieveBfStatus = planVersion => {
    dpProcessMgmtService
      .getBfStatus(planVersion)
      .then(response => {
        if (response.data) {
          //170 전 단계 3분 wait
          if (response.data.data === null) {
            setStatusValue(70);
            useBf.current = true;
          } else {
            if (response.data.data.statusCd === PROCESSING) {
              //PROCESSING
              const seqNum = response.data.data.seqNum;
              setStatusValue(valueByStatus[seqNum])
              useBf.current = true;
            } else if (response.data.data.statusCd === ERROR) {
              //ERROR
              useBf.current = false;
              const errorStatus = response.data.data.errorStatus;
              setStatusValue(valueByStatus[errorStatus])
              setCircleStyle({ ...circleStyle, color: "red"})
              setOverwriteAlertFlg(true);
            } else {
              //COMPLETE
              useBf.current = false;
              setStatusValue(100);
              setOverwriteAlertFlg(true);
            }
          }
        } else {
          //NOTHING
          setStatusValue(100);
          setOverwriteAlertFlg(false);
        }
      })
      .catch(e => {
        debug(e);
        useBf.current = false;
        setStatusValue(0);
        setOverwriteAlertFlg(false);
      });
  };

  const retrievePlanVersion = () => {
    planVersionService.getAll({ moduleCd: codeUtil.module.DP, statusCds: [RELEASING, RUNNING]})
      .then(response => {
        const { results } = response.data;
        if (isEmpty(results)) {
          return;
        }

        for (const result of results) {
          if (result.statusCd === RUNNING) {
            if (result.parent === null) {
              setIsStartDp(true);
            } else {
              setIsCreateVersion(true);
            }
          }
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const setInitialCard = (initialDemand, updateDatas) => {
    const arrays = [];
    const tooltip = [];
    const initialKeys = keys(initialDemand);
    if (size(initialKeys) > 0) {
      for (let [title, content] of entries(initialDemand)) {
        if (title === "custLevel" || title === "itemLevel") {
          continue;
        }
        if (!isArray(content)) {
          if (title !== "defaultValue") {
            if (title === "custLevelId") {
              title = "custLevel";
            }
            if (title === "itemLevelId") {
              title = "itemLevel";
            }
            const level = hrchyLevels.filter(lvel => isEqual(lvel.id, content));
            const contentNm = isEmpty(level) || title === "rollingRange" ? content : level[0]?.name ?? content;
            arrays.push(cardLine(title, contentNm));
          }
        } else {
          if (title !== "layout") {
            arrays.push(cardLine(title));
            if (title === BASELINE_FORECAST) {
              setInitialBfFlg(true);
              const values = Object.values(initialDemand[BASELINE_FORECAST]);
              const measure = [];
              for (const value of values) {
                measure.push(Object.keys(value)[0]);
              }
              setOverwriteMeasure(measure);
            } else {
              setInitialBfFlg(false);
            }
            const computeFields = content;
            computeFields?.forEach(computeField => {
              for (const [key, val] of entries(computeField)) {
                const measureKey = dpMeasure[key]?.name;
                if (measureKey) {
                  tooltip.push(cardLineOfTemplate(measureKey, val, title));
                }
              }
            });
          }
        }
      }
    }
    updateDatas[DP_INITIAL_VALUE_INDEX].text = arrays;
    updateDatas[DP_INITIAL_VALUE_INDEX].sText = tooltip;
  }

  /*
   * 서버 응답으로 카드 전체를 새로 만들어 돌려준다(순수 함수 — 호출마다 새 배열, state·템플릿·이전 배열을 건드리지 않는다).
   * 호출한 쪽이 setCardDatas(결과) 로 통째로 교체한다.
   */
  const setVerDatas = (version, userStatus, bfStatus, bfName) => {
    const datas = makeCardDatas();
    if (version && userStatus) {
      const { PLAN_START_DATE, PLANNING_RANGE, BUCKET_UNIT, START_DAY, INITIAL_DEMAND } = version.planConfigObj;
      const comRate = status => {
        if (status) {
          let completed = 0;
          const complete = status.reduce((_pre, cur) => {
            if (isEqual(cur.apvlStatusCd, COMPLETED)) completed++;
            return completed;
          }, 0);
          if (complete) return Math.floor((complete / size(status)) * 100);
        }
        return 0;
      };

      for (let idx = 0; datas.length > idx; idx++) {
        if (idx === DP_INFO_INDEX) {
          const arrays = [];
          const time = version.execTs ?? version.updatedTs;
          arrays.push(cardLine("forecastStartTime", formatDateTime(time)));
          arrays.push(cardLine("dpVersionName", version.name));
          if (bfName) {
            arrays.push(cardLine("bfVersionName", bfName));
          }
          datas[idx].text = arrays;
        } else if (idx === DP_INITIAL_VALUE_INDEX) {
          setInitialCard(INITIAL_DEMAND, datas);
        }else if (idx === BF_RUN_TIME_INDEX) {
          if (bfStatus?.statusCd === PROCESSING) {
            datas[idx].text = [cardLine("startTs", formatDateTime(bfStatus?.startTs)), cardLine("endTs", "")];
          } else {
            datas[idx].text = [cardLine("startTs", formatDateTime(bfStatus?.startTs)), cardLine("endTs", formatDateTime(bfStatus?.endTs))];
          }
        } else if (idx === DP_HORISON_INDEX) {
          const startDate = new ZDate(PLAN_START_DATE);
          const format = codeUtil.isWeekBucket(BUCKET_UNIT) ? "yyyy-MM-dd" : "yyyy-MM";
          const endDate = calculateRangeDate(PLAN_START_DATE, BUCKET_UNIT, START_DAY, PLANNING_RANGE, true);
          datas[idx].text = [
            cardLineOfKey("bucket", BUCKET_UNIT),
            codeUtil.isWeekBucket(BUCKET_UNIT) ? cardLineOfKey("startDayOfWeek", START_DAY) : null,
            cardLine("startTs", formatDate(startDate, format)),
            cardLine("endTs", formatDate(endDate, format))
          ];
        } else if (idx === TOTAL_COMPLETE_INDEX) {
          datas[idx].text = `${comRate(userStatus)}%`;
        }
      }
    }
    return datas;
  };

  const settingChartDatas = (datas, baseDates) => {
    const chartData = [];
    const headerSize = size(baseDates);
    const bf = includes(userRoles, roles.APP_BF) ? datas[BF_AMT.name] : null;
    const mf = datas[MF_AMT.name];

    const getFormatDate = index => {
      if (Array.isArray(baseDates)) {
        return baseDates[index];
      } else {
        const sortHeaders = Object.keys(baseDates).sort();
        return baseDates[sortHeaders[index]];
      }
    };

    for (let idx = 0; idx < headerSize; idx++) {
      const cData = {};
      if (bf) {
        cData[BF_AMT.name] = bf[idx];
      }
      if (mf) {
        cData[MF_AMT.name] = mf[idx];
      }

      cData.yyyymmdd = getFormatDate(idx);
      chartData.push(cData);
    }
    return chartData;
  };

  const settingGridDatas = (userStatus, hasCurVer) => {
    const gridList = [];
    if (userStatus.length > 0) {
      let allCompletedFlg = true;
      for (const postnStatus of userStatus) {
        const postn = {};
        postn.user = postnStatus.postnDto;

        if (hasCurVer) {
          postn.status = postnStatus.apvlStatusCd;
        } else {
          postn.status = NOTSTARTED;
        }

        postn.date = isEqual(postnStatus.apvlStatusCd, COMPLETED) ? formatDate(postnStatus.updatedTs, "yyyy-MM-dd") : null;
        postn.actions = [];

        if (isEqual(postnStatus.apvlStatusCd, INPROGRESS)) {
          postn.actions.push(true);
        } else {
          postn.actions.push(false);
        }

        if (!isEqual(postnStatus.apvlStatusCd, COMPLETED)) {
          allCompletedFlg = false;
        }
        gridList.push(postn);
      }
      if (allCompletedFlg) {
        setAllCompleted(true);
      }
    }
    return gridList;
  };

  const retrieveDpProcess = () => {
    const mutationAtCall = versionMutation.current;
    // 조회를 여러 번 띄우면(생성 직후 재조회 등) 가장 마지막에 띄운 조회의 응답만 반영한다.
    // 먼저 띄운 조회가 나중에 도착해 더 최신인 응답을 옛 값으로 덮어쓰지 않도록.
    const requestSeq = ++dpProcessRequestSeq.current;
    dpProcessMgmtService
      .get()
      .then(response => {
        if (requestSeq !== dpProcessRequestSeq.current) {
          return;
        }
        /*
         * 늦게 도착한 "생성 전" 응답은 버린다.
         * 버전 생성은 이 조회와 makeDemand 를 동시에 띄우는데, 이쪽이 측정치 집계까지 돌아 더 늦게 끝날 수 있다.
         * 그러면 makeDemand 가 세운 hasCurVersion(=true) 을 생성 전 상태(false)로 되돌려
         * **버전을 만들었는데도 START DP 가 다시 눌리는** 상태가 된다.
         * 서버는 아직 SCENARIO 가 아닌 버전을 못 찾고 직전 RELEASED 버전으로 폴백하므로(PlanVersionService.getDpRecentVersion)
         * 카드 값도 옛 버전으로 되돌아간다.
         */
        if (mutationAtCall !== versionMutation.current) {
          return;
        }
        if (response.data?.data) {
          const { planVersion, measureVals, headers, weekHeaders } = response.data.data;
          const paramHeaders = codeUtil.isWeekBucket(planVersion.planConfigObj?.BUCKET_UNIT) ? weekHeaders : headers;
          const { userStatus: userStatusVal, APPROVAL_ACCESS_CONTROL: approvalAccessControl } = response.data;
          const bfStatus = response.data?.status ?? 0;
          const bfVersionName = response.data?.bfName;
          setRefBfName(bfVersionName)
          setApprovalAccessControlFlg(approvalAccessControl);
          setSelectVersion(planVersion);
          currentVersion.current = planVersion;
          if (planVersion.statusCd === SCENARIO && planVersion.parent === null) {
            setIsCurrent(true);
          }
          setCardDatas(setVerDatas(planVersion, userStatusVal, bfStatus, bfVersionName));
          const hasCurVerFlg = isEqual(planVersion.statusCd, SCENARIO) && !isEqual(planVersion.moduleCd, DEFAULT) && planVersion.parent === null;
          setHasCurVersion(hasCurVerFlg);
          setChartDatas(settingChartDatas(measureVals, paramHeaders));
          setGridDatas(settingGridDatas(userStatusVal, hasCurVerFlg));

          if (planVersion && !useBf.current) {
            retrieveBfStatus({ planVersionId: planVersion.id });
          }
        } else {
          setHasCurVersion(false);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const startBF = () => {
    setCircleStyle({ transform: "rotate(440deg)"})
    setSnackInfo({ open: true, severity: confirm, content: t("MSG_ConfirmStartBF") });
    setStatusValue(10);
    useBf.current = true;
    // BF 시작 전에 띄운 조회(retrieveDpProcess)의 응답은 "BF 시작 전" 상태다. 늦게 도착해 방금 세운 BF Run Time 시작 시각을
    // 비워 버리지 않도록 순번을 올려 그 응답을 버리게 한다(retrieveDpProcess 의 mutationAtCall 비교).
    versionMutation.current += 1;
    // BF Run Time 카드만 최신 state 위에 덮어쓴다(다른 카드는 건드리지 않는다).
    const setBfRunTime = (startTs, endTs) => patchCardDatas(next => {
      if (next?.[BF_RUN_TIME_INDEX]) {
        next[BF_RUN_TIME_INDEX].text = [cardLine("startTs", startTs), cardLine("endTs", endTs)];
      }
    });
    let removeRef = false
    if (refBfName) {
      removeRef = true
    }
    setBfRunTime(formatDateTime(new ZDate()), "");
    dpProcessMgmtService
      .getParameters({ overwriteFlg, removeRef })
      .then(response => {
        if (response?.data?.message) {
          setStatusValue(0);
          useBf.current = false;
          setBfRunTime("", "");
          const { message } = response.data;
          setSnackInfo({ open: true, severity: message.type, content: `${t(message.message)}` });
        }
      })
      .catch(e => {
        debug(e);
        setStatusValue(0);
        useBf.current = false;
        setBfRunTime("", "");
        setSnackInfo({ open: true, severity: networkError, content: t("MSG_NetworkError") });
      })
      .finally(() => {
        retrieveBfStatus({ planVersionId: selectVersion.id });
      });
  };

  const retrievePostnChildClsr = postnCds => {
    postnService
      .getChildClsr([...postnCds])
      .then(response => {
        setPostnByUser(response.data);
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrievePostnByUser = () => {
    userService
      .getUser({ username: userData.username })
      .then(response => {
        if (response.data?.results) {
          const user = response.data.results[0];
          if (user.postn) {
            setUserInfo(user.postn);
            const myPostnCds = user?.postn.map(postn => postn.postnCd);
            retrievePostnChildClsr(myPostnCds);
          }
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveUserRoles = () => {
    userService.getUserAccessRoles({ username: userData.username })
      .then(
        (response) => {
          const userAccess = response.data;
          setUserRoles(userAccess?.roles);
        },
      )
      .catch(e => { debug(e); });
  };

  const retrieveParameters = () => {
    const param = {
      moduleCd: "DP",
      activeFlg: true,
    };
    planConfigService
      .getAll(param)
      .then(response => {
        if (response.data) {
          for (const result of response.data.results) {
            if (result.configCd === BF_PARAMETER) {
              setBfParameters(result);
            }
          }
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const confirmPlan = postnDto => {
    postnDto.masterConfirm = true;
    setComfirmPostnCd(postnDto.postnCd);
    setFinishSave(true);
    const params = { ...postnDto };
    postnService
      .confirm([params])
      .then(response => {
        if (response?.data) {
          setFinishSave(false);
          setComfirmPostnCd("");
          const { type, message } = response.data;
          setSnackInfo({ open: true, severity: type, content: t(message) });
          retrieveDpProcess();
        }
      })
      .catch(e => {
        setFinishSave(false);
        setComfirmPostnCd("");
        setSnackInfo({ open: true, severity: saveFail, content: t("MSG_ConfirmFailed") });
        debug(e);
      });
  };

  const cancelPlan = postnDto => {
    const params = { ...postnDto };
    postnService
      .cancel(params)
      .then(response => {
        if (response?.data) {
          const { type, message } = response.data;
          setSnackInfo({ open: true, severity: type, content: t(message) });
          retrieveDpProcess();
        }
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: saveFail, content: t("MSG_ConfirmCancelFailed") });
        debug(e);
      });
  };

  const valueFormatter = value => {
    if (!isEmpty(value)) {
      if (isEqual(value, COMPLETED)) {
        return t("completed");
      } else if (isEqual(value, INPROGRESS)) {
        return t("inProgress");
      }
      return t("notStarted");
    }
    return value;
  };

  const BtnRenderer = params => {
    if (params.data?.actions?.length > 0) {
      const isConfirm = params.data.actions[0];
      const postnDto = params.node?.data?.user;
      const status = params.data?.status;

      let hasApproval = false;
      if (postnByUser?.length > 0) {
        const postnsByuser = postnByUser.concat(userInfo);
        hasApproval = postnsByuser.some(postn => isEqual(postn.postnCd, postnDto.postnCd));
      } else {
        hasApproval = userInfo.some(postn => isEqual(postn.postnCd, postnDto.postnCd));
      }

      if (gridDatas && userInfo && !gridDatas.find((grid) => isEqual(grid.user?.postnCd, userInfo[0]?.postnCd))) {
        hasApproval = false;
      }

      return (
        <>
          {finishSave && hasApproval && isConfirm && isCurrent && confirmPostnCd === postnDto.postnCd ? (
            <LoadingButton
              sx={{ ml: 1, p: "2px 10px", mt: "2px", fontSize: FS.SUB }}
              startIcon={<PlayArrowIcon />}
              loading
              loadingPosition="start"
              variant="outlined">
              {t("approve")}
            </LoadingButton>
          ) : (
            <Button
              variant="outlined"
              disabled={!(isCurrent && isConfirm && hasApproval) || !approvalAccessControlFlg}
              startIcon={<CheckCircleOutlineIcon />}
              size="small"
              sx={{ ml: 1, p: "2px 10px", mt: "2px", fontSize: FS.SUB }}
              onClick={() => confirmPlan(postnDto)}>
              {t("approve")}
            </Button>
          )}
          <Button
            variant="outlined"
            disabled={!(isCurrent && hasApproval && isEqual(COMPLETED, status)) || !approvalAccessControlFlg}
            startIcon={<Close />}
            color={"warning"}
            size="small"
            sx={{ ml: 1, p: "2px 10px", mt: "2px", fontSize: FS.SUB }}
            onClick={() => cancelPlan(postnDto)}>
            {t("reject")}
          </Button>
        </>
      );
    }
    return null;
  };

  const statusRenderer = params => {
    if (params.data?.status) {
      const color = isEqual(params.data?.status, INPROGRESS) ? yellow[600] : isEqual(params.data?.status, COMPLETED) ? green[500] : red[500];
      const visible = approvalAccessControlFlg ? "visible" : "hidden";
      return (
        <Box sx={{ display: "flex", alignItems: "center", mt: "5px", visibility: visible }}>
          <Typography sx={{ fontSize: FS.SUB, mr: 1 }}>{valueFormatter(params.data?.status)}</Typography>
          <Circle
            fontSize="small"
            sx={{ color }}
          />
        </Box>
      );
    }
    return null;
  };

  const visibleRenderer = param => {
    if (approvalAccessControlFlg) {
      return param.value;
    } else {
      return "";
    }
  };

  const colDefs = [
    {
      headerName: "planStatus",
      field: "status",
      width: 300,
      type: ["nonEditableColumn"],
      comparator: (val1, val2) => {
        if (val1 === val2) {
          return 0;
        } else if (val1 > val2) {
          return 1;
        }
        return -1;
      },
      cellRenderer: statusRenderer,
      valueFormatter: params => valueFormatter(params?.value),
    },
    {
      headerName: "completionDate",
      field: "date",
      type: ["nonEditableColumn"],
      width: 300,
      cellRenderer: visibleRenderer,
    },
    {
      headerName: "approvalProcess",
      field: "actions",
      type: ["nonEditableColumn", "defaultValueFormatter"],
      width: 350,
      cellRenderer: BtnRenderer,
    },
  ];

  const getRowHeight = () => {
    return 35;
  };

  const handleVersion = () => {
    setVersionOpen(!versionOpen);
  };

  const handleScenario = () => {
    setScenarioOpen(!scenarioOpen);
  };

  const closeBfDialog = () => setDialogInfo({ open: false, title: "", content: "" });

  const handleDialog = action => {
    if (action === "CONFIRM") {
      if (bfParameters.length <= 0) {
        setSnackInfo({ open: true, severity: noMatchCondition, content: t("MSG_SetBfParameter") });
      } else {
        startBF();
      }
    }
  };

  const linkWorkbench = () => {
    const reduxKey = "DP_WORKBENCH";
    reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
  };

  const linkBfReview = () => {
    const reduxKey = "BF_REVIEW";
    reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
  };

  const linkScenarioComparision = () => {
    const reduxKey = "SCENARIO_COM_DB";
    reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
  };

  const startAutoBf = (version) => {
    if (version?.bfAutoFlg) {
      useBf.current = true;
      if (bfParameters.length <= 0) {
        useBf.current = false;
        setSnackInfo({ open: true, severity: noMatchCondition, content: t("MSG_SetBfParameter") });
      } else {
        setTimeout(() => {
          setHasCurVersion(true);
          currentVersion.current = version;
          startBF();
        }, 2000);
      }
    }
  };

  const startMakeDemand = createdVersion => {
    const reduxStateWorkbench = reduxUtil.createObjectState("DP_WORKBENCH");
    setMakeDemand(true);
    if (reduxViewState) {
      reduxViewState.makeDemand = true;
    }
    currentVersion.current = createdVersion;
    planVersionService
      .makeDemand(createdVersion)
      .then(response => {
        /*
         * 백엔드는 실패 사유(이미 실행 중·버전 없음)를 200 본문(message)에 담아 준다.
         * 예전엔 이걸 보지 않고 무조건 성공 메시지를 띄워, 버전이 만들어지지 않았는데도
         * "버전 생성됨" 이 뜨고 버튼만 그대로였다. 실패면 여기서 끝내고 아래 finally 가 화면을 서버 상태로 맞춘다.
         */
        const failMessage = response?.data?.message;
        if (failMessage) {
          setIsStartDp(false);
          setMakeDemand(false);
          setSnackInfo({ open: true, severity: saveFail, content: t(failMessage.message ?? "MSG_CreateVersionFailed") });
          return;
        }
        if (response) {
          setIsStartDp(false);
          setMakeDemand(false);
          setHasCurVersion(true);
          if (reduxViewState) {
            reduxViewState.makeDemand = false;
          }
          if (workbench) {
            workbench.endMakeDemand = true;
          }
        }
        setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_CreateVersion") });
        const { PLAN_START_DATE, PLANNING_RANGE, BUCKET_UNIT, START_DAY, INITIAL_DEMAND } = createdVersion.planConfigObj;
        const startDate = new ZDate(PLAN_START_DATE);
        const format = codeUtil.isWeekBucket(BUCKET_UNIT) ? "yyyy-MM-dd" : "yyyy-MM";
        const endDate = calculateRangeDate(PLAN_START_DATE, BUCKET_UNIT, START_DAY, PLANNING_RANGE, true)

        const list = [];
        list.push(cardLine("forecastStartTime", formatDateTime(createdVersion.createdTs)));
        list.push(cardLine("dpVersionName", createdVersion.name));
        if (createdVersion?.bfName) {
          setRefBfName(createdVersion?.bfName)
          list.push(cardLine("bfVersionName", createdVersion.bfName));
        } else {
          setRefBfName("")
        }
        // setInitialCard 는 setState(부수효과)도 부르므로 업데이터 안이 아니라 임시 배열에 먼저 만든 뒤 옮겨 담는다.
        const initialCardDatas = [];
        initialCardDatas[DP_INITIAL_VALUE_INDEX] = {};
        setInitialCard(INITIAL_DEMAND, initialCardDatas);

        // DP 정보 / 기간 / 초기값 카드만 최신 state 위에 덮어쓴다 — 동시에 도는 startBF 가 바꾼 BF Run Time 카드는 건드리지 않는다.
        patchCardDatas(next => {
          next[DP_INFO_INDEX].text = list;
          next[DP_HORISON_INDEX].text = [
            cardLineOfKey("bucket", BUCKET_UNIT),
            codeUtil.isWeekBucket(BUCKET_UNIT) ? cardLineOfKey("startDayOfWeek", START_DAY) : null,
            cardLine("startTs", formatDate(startDate, format)),
            cardLine("endTs", formatDate(endDate, format))
          ];
          next[DP_INITIAL_VALUE_INDEX].text = initialCardDatas[DP_INITIAL_VALUE_INDEX].text;
          next[DP_INITIAL_VALUE_INDEX].sText = initialCardDatas[DP_INITIAL_VALUE_INDEX].sText;
        });
      })
      .catch(e => {
        debug(e);
        setIsStartDp(false);
        setMakeDemand(false);
        setHasCurVersion(true);
      })
      .finally(() => {
        // 이 시점 이전에 띄운 조회 응답은 전부 "생성 전" 상태다 — retrieveDpProcess 가 알아보고 버린다.
        versionMutation.current += 1;
        if (workbench) {
          workbench.endMakeDemand = true;
          reduxViewState.makeDemand = false;
        }
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...reduxViewState }));
        reduxDispatch(reduxUtil.updateViewState({ ...reduxStateWorkbench, ...workbench }));
        /*
         * 버전 상태(READY→SCENARIO)는 서버가 makeDemand 끝에 바꾼다. 한 번 더 읽어 화면(버튼·카드·승인 목록)을
         * 서버가 확정한 상태로 맞춘다 — 생성이 실패했다면 여기서 다시 START DP 가 열린다.
         * BF 자동 실행 중이면 BF Run Time 카드를 startBF 가 들고 있으므로 건드리지 않는다(버튼 상태는 위에서 이미 세웠다).
         */
        if (!useBf.current) {
          retrieveDpProcess();
        }
      });
  };

  const retrieveHrchyConfigs = () => {
    const params = {};
    hrchyConfigService.getAll(params)
      .then((response) => {
        const { results } = response?.data;
        setHrchyLevels([...results, item, customer]);
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        setLoading(true);
      });
  };

  useEffect(() => {
    // 현재 화면을 viewState 에 등록. 렌더 도중 dispatch 하면
    // "Cannot update a component while rendering" 경고와 함께 리렌더 churn 이 생겨
    // 다른 컴포넌트(App)의 상태 업데이트(탭 전환 등)가 유실될 수 있어 mount 시 1회만 실행한다.
    reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));
    retrieveUserRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEmpty(userRoles)) {
      retrievePostnByUser();
      retrieveParameters();
      retrieveHrchyConfigs();
      retrievePlanVersion();
      setCircleStyle({ transform: "rotate(440deg)"})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles]);

  useEffect(() => {
    if (loading) {
      retrieveDpProcess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (reload) {
      retrieveDpProcess();
      if (useBf.current) {
        setTimeout(() => {
          setSnackInfo({ open: true, severity: failAutoRun, content: t("MSG_FailAutoRun") });
        }, 3000);
      }
      startMakeDemand(selectVersion);
      startAutoBf(selectVersion);
      setReload(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  useEffect(() => {
    statusRef.current = statusValue;
  }, [statusValue]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (currentVersion.current.id) {
        retrieveBfStatus({ planVersionId: currentVersion.current.id });
      }
      if (statusRef.current === 100) {
        clearInterval(timer);
      }
    }, 120000);
    return () => {
      clearInterval(timer);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isStartDp) {
      window.dispatchEvent(new CustomEvent('export-started'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStartDp]);

  useEffect(() => {
    if (isCreateVersion) {
      window.dispatchEvent(new CustomEvent('export-started'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateVersion]);

  const BfButton = ({ bfFlg }) => {
    let message = "";
    let subMessage = "";
    if (refBfName) {
      message = t("MSG_RegenerateBf", { VERSION_NAME: refBfName });
      if (initialBfFlg) {
        let measureName = "";
        for (const measure of overWriteMeasure) {
          measureName += t(codeUtil.dpMeasure[measure].name);
          if (overWriteMeasure.indexOf(measure) !== overWriteMeasure.length - 1) {
            // 구분자 뒤 공백은 호출측이 붙인다 — 번역키 ","(ja 는 "、")는 문장부호만 준다(InventoryManagementOverview 와 같은 방식)
            measureName += `${t(",")} `
          }
        }
        subMessage = t("MSG_BfOverwriteSave", { VERSION_NAME: selectVersion?.name, N: measureName })
      }
    }
    if (initialBfFlg && overwriteAlertFlg) {
      let measureName = "";
      for (const measure of overWriteMeasure) {
        measureName += t(codeUtil.dpMeasure[measure].name);
        if (overWriteMeasure.indexOf(measure) !== overWriteMeasure.length - 1) {
          measureName += `${t(",")} `
        }
      }
      message = t("MSG_BfOverwriteSave", { VERSION_NAME: selectVersion?.name, N: measureName })
    }
    return includes(userRoles, roles.APP_BF) ?
      bfFlg ? (
        <LoadingButton
          sx={{ ml: 1, width: "30%" }}
          startIcon={<PlayArrowIcon />}
          loading
          loadingPosition="start"
          variant="outlined">
          {t("runningBf")}
        </LoadingButton>
      ) : (
        <Button
          variant="contained"
          disabled={!hasCurVersion || allCompleted}
          startIcon={<PlayArrowIcon />}
          size="large"
          color={"warning"}
          sx={{ ml: 1, width: "30%" }}
          onClick={() => {
            if (refBfName) {
              if (initialBfFlg) {
                const checkBox = {checkBox: initialBfFlg,  checkBoxMessage: subMessage}
                setDialogInfo({ open: true, title: "confirm", checkBox, content: message});
              } else {
                setDialogInfo({ open: true, title: "confirm", content: message});
              }
            } else {
              const checkBox = {checkBox: initialBfFlg && overwriteAlertFlg,  checkBoxMessage: message}
              setDialogInfo({ open: true, title: "confirm", checkBox, content: t("MSG_StartBF")});
            }
          }}>
          {t("startBF")}
        </Button>
      ) : null;
  };

  return (
    <Box id="dp-process-mgmt" sx={{ height: "100%", minWidth: 980 }}>
      <SplitterContainer initialSizes={[4, 2]} collapseDirection='up'>
        {/* 세로 배분: 카드 줄과 버튼 줄은 내용만큼만 차지하고 차트가 남는 높이를 먹는다.
            예전엔 카드 줄이 height:140 고정이라 Initial Value 처럼 줄이 많은 카드가 아래에서 잘렸다.
            줄 수가 설정(측정치 개수)·언어에 따라 달라지므로 고정 높이로는 막을 수 없다.

            ⚠ boxSizing 을 반드시 border-box 로. 전역 `.chart-area` 가 `height: calc(100% - 20px)` +
            `padding-top: 10px` 인데 기본값(content-box)이면 실제 높이가 100%-10px 이 되어 **판보다 10px 크다**.
            예전엔 차트가 `calc(98% - 190px)` 로 덜 채워서 그 10px 이 묻혔지만, flex 로 꽉 채우니
            버튼 줄 아래가 잘렸다(지적받음). */}
        <Box className="wrap-dp-process-mgmt chart-area"
          sx={{ minHeight: 200, height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column" }} >
          <Box id="dp-process-mgmt-chart" sx={{ width: "100%", flex: "1 1 0", minHeight: 180 }}>
            <ChartComposed
              xAxis={["yyyymmdd"]}
              data={chartDatas}
              options={{ minHeight: 180, isAmt: true }}
              dataSeries={getChartSeries(includes(userRoles, roles.APP_BF))}
              tooltipAnimation={false}
            />
          </Box>
          <Box id="dp-process-mgmt-card" sx={{ display: "flex", width: "100%", flexShrink: 0 }}>
            {cardDatas.length > 0 &&
              cardDatas.map((cardData, idx) => {
                const maRight = isEqual(idx, 4) ? 0 : "1%";
                const classNm = isEqual(idx, 2) || isEqual(idx, 1) ? "box-bf-runtime" : null;
                // const widthCard = isEqual(idx, 0) ? "20%" : "50%";
                if (!includes(userRoles, roles.APP_BF) && idx === 2) {
                  return null;
                }
                return (
                  <Box
                    className={classNm}
                    key={cardData.key}
                    sx={{ width: "50%", mr: maRight, mb: 2 }}>
                    <AppCard
                      key={cardData.key}
                      sx={{ height: "100%" }}>
                      {/* MUI 기본은 마지막 CardContent 만 pb 24 라 위(16)와 어긋난다 — 맞춰 준다 */}
                      <CardContent sx={{ display: "flex", "&:last-child": { pb: 2 } }}>
                        <Box>
                          {/* 여러 줄 상세 정보형 카드 — 제목=진한 헤더(CARD_HEADER_SX), 상세=회색 13px(제목보다 물러남). */}
                          <Typography
                            key={`typo${cardData.key}1`}
                            sx={{ ...CARD_HEADER_SX, mb: 0.75 }}>
                            {t(cardData.titleKey)}
                          </Typography>
                          {isArray(cardData.text) ? (
                            cardData.text.map((text, i) => {
                              return (
                                <Typography
                                  key={`typo${cardData.key}${i + 9}`}
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontSize: FS.TITLE }}>
                                  {cardLineText(t, text)}
                                </Typography>
                              );
                            })
                          ) : (
                            <Typography
                              key={`typo${cardData.key}2`}
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: FS.TITLE }}>
                              {cardLineText(t, cardData.text)}
                            </Typography>
                          )}
                          {cardData?.sText?.map((stext, i) => {
                            return (
                              <Typography
                                key={`small${cardData.key}${i + 9}`}
                                variant="body2"
                                sx={{ fontSize: "var(--fs-title) !important" }}
                                color="text.secondary">
                                {cardLineText(t, stext)}
                              </Typography>
                            );
                          })
                          }
                        </Box>
                        {cardData.key === "third" ? (
                          <Box className={"wrap-cir-progress"} sx={{ mt: 0.5, ml: "auto" }}>
                            <Box sx={{ position: "relative", display: "inline-flex" }}>
                              <CircularProgress
                                variant="determinate"
                                thickness={4}
                                value={statusValue}
                                size="5rem"
                                style={circleStyle}
                              />
                              <Box
                                sx={{
                                  top: 0,
                                  left: 0,
                                  bottom: 0,
                                  right: 0,
                                  position: "absolute",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}>
                                <Typography
                                  variant="caption"
                                  fontWeight="bold"
                                  component="div"
                                  color="text.secondary">
                                  {`${statusValue}%`}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          <Box></Box>
                        )}
                      </CardContent>
                    </AppCard>
                  </Box>
                );
              })}
          </Box>
          {/* 아래 여백은 장식이 아니다 — 스플리터 접기 손잡이(알약, .splitter-button)가 바 중앙에 걸려
              위쪽 팬 안으로 10px 들어온다. 버튼 줄이 팬 바닥에 붙어 있으면 그 알약이 가운데 버튼
              두 개(REVIEW BASELINE / ADJUST DEMAND)의 모서리를 파먹는다. 카드 줄과 같은 16px 을 비워
              알약이 빈 자리에 뜨게 한다. */}
          <Box sx={{ display: "flex", flexShrink: 0, mb: 2 }}>
            { isStartDp ?
              <LoadingButton
                sx={{ ml: 1, width: "30%" }}
                startIcon={<PlayArrowIcon />}
                loading
                loadingPosition="start"
                variant="outlined">
                {t("startDP")}
              </LoadingButton>
              :
              <Button
                variant="contained"
                disabled={hasCurVersion}
                startIcon={<PlayArrowIcon />}
                size="large"
                color={"warning"}
                sx={{ width: "30%" }}
                onClick={handleVersion}>
                {t("startDP")}
              </Button>
            }
            <BfButton bfFlg={useBf.current} />
            {includes(userRoles, roles.APP_BF) && isPermitted("BF_REVIEW") ? (
              <Button
                variant="contained"
                startIcon={<AssessmentIcon />}
                size="large"
                sx={{ ml: 1, width: "30%" }}
                onClick={linkBfReview}>
                {t("reviewBaseline")}
              </Button>
            ) : null}
            {makeDemand ? (
              <LoadingButton
                sx={{ ml: 1, width: "30%" }}
                startIcon={<PlayArrowIcon />}
                loading
                loadingPosition="start"
                variant="outlined">
                {t("adjustDemand")}
              </LoadingButton>
            ) : isPermitted("DP_WORKBENCH") ? (
              <Button
                variant="contained"
                startIcon={<AssessmentIcon />}
                size="large"
                sx={{ ml: 1, width: "30%" }}
                onClick={linkWorkbench}>
                {t("adjustDemand")}
              </Button>
            ) : null}
            { isCreateVersion ?
              <LoadingButton
                sx={{ ml: 1, width: "30%" }}
                startIcon={<PlayArrowIcon />}
                loading
                loadingPosition="start"
                variant="outlined">
                {t("createScenario")}
              </LoadingButton>
              :
              <Button
                variant="contained"
                disabled={!hasCurVersion || allCompleted || isCreateVersion || isStartDp}
                startIcon={<AssessmentIcon />}
                size="large"
                color={"warning"}
                sx={{ ml: 1, width: "30%" }}
                onClick={handleScenario}>
                {t("createScenario")}
              </Button> }
            {isPermitted("SCENARIO_COM_DB") && (
              <Button
                variant="contained"
                disabled={!hasCurVersion}
                startIcon={<StopCircleOutlinedIcon />}
                size="large"
                color={"warning"}
                sx={{ ml: 1, width: "30%" }}
                onClick={linkScenarioComparision}>
                {t("releaseDp")}
              </Button>
            )}
          </Box>
        </Box>
        <Box id="dp-process-mgmt-grid" className="ag-theme-balham chart-grid-area" sx={{ height: "calc(100% - 21px)", minHeight: 150, mt: 2 }}>
          <AgGridReact
            rowData={approvalAccessControlFlg ? gridDatas : []}
            columnDefs={GridUtils.getColumnDefs(colDefs)}
            {...DefaultGridSetting({ title })}
            defaultColDef={{
              flex: 1,
              minWidth: 30,
              resizable: true,
            }}
            getRowHeight={getRowHeight}
            treeData
            getDataPath={data => data.user.postnPath}
            groupDefaultExpanded={-1}
            suppressAggFuncInHeader
            autoGroupColumnDef={{
              headerName: t("postn"),
              minWidth: 250,
              editable: false,
              cellRendererParams: {
                suppressCount: true,
              },
              valueGetter: params => {
                return params.data ? params.data.user.name : params.node.key;
              },
            }}
          />
        </Box>
      </SplitterContainer>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      {/* START BF 확인 — 확인 박스가 아니라 "실행 전 안내 + 덮어쓰기 옵션"이라 설정 모달 셸을 쓴다.
          주 버튼은 저장이 아니라 실행이므로 라벨/아이콘을 그 동작(Start BF)으로 바꿨다. */}
      <BaseConfigModal
        open={dialogInfo.open}
        onClose={closeBfDialog}
        title={t("startBF")}
        width={440}
        saveLabel={t("startBF")}
        saveIcon={<PlayArrowIcon />}
        onSave={() => {
          handleDialog("CONFIRM");
          closeBfDialog();
        }}>
        {splitLines(dialogInfo.content).map((line, idx) => {
          const text = line.trim();
          // 빈 줄은 문단 사이 여백으로 남긴다 — 예전 Linebreak 는 통째로 버려 문단 구분이 사라졌다
          return text ? (
            <Box key={`msg${idx}`} sx={{ fontSize: FS.TITLE, lineHeight: 1.6 }}>{text}</Box>
          ) : (
            <Box key={`msg${idx}`} sx={{ height: 8 }} />
          );
        })}
        {dialogInfo.checkBox?.checkBox && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ ...MODAL_SECTION_LABEL_SX, pb: 1 }}>{t("option")}</Box>
            {/* 라벨이 여러 줄이라 체크박스를 첫 줄에 맞춘다(가운데 정렬이면 문단 한가운데 떠 보인다) */}
            <FormControlLabel
              sx={{
                display: "flex", alignItems: "flex-start", ml: -0.75, mr: 0,
                "& .MuiCheckbox-root": { pt: 0.125 },
                "& .MuiFormControlLabel-label": { fontSize: FS.TITLE, lineHeight: 1.6 },
              }}
              control={
                <Checkbox
                  size="small"
                  checked={overwriteFlg}
                  onChange={event => setOverwriteFlg(event.target.checked)}
                />
              }
              label={joinLines(dialogInfo.checkBox.checkBoxMessage)}
            />
          </Box>
        )}
      </BaseConfigModal>
      <DpVersionModal
        open={versionOpen}
        existVersion={selectVersion}
        selectVersion={setSelectVersion}
        close={handleVersion}
        reload={setReload}
        userRoles={userRoles}
        running={setIsStartDp}
      />
      <DpCreateScenarios
        open={scenarioOpen}
        close={handleScenario}
        userRoles={userRoles}
        running={setIsCreateVersion}
      />
    </Box>
  );
};

export default withTranslation()(DpProcessMgmt);
